import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import {
  BankingCumulativeReturnAnalytics,
  BankingCumulativeSupplyAnalytics,
  BankingCumulativeSupplyDestinations,
  BankingCumulativeSupplySources,
  BankingSupplySource,
} from "../../../../models/banking";
import { SupplyChart } from "../components/SupplyChart";
import { PeriodBreakdownTable } from "../components/PeriodBreakdownTable";
import {
  deriveCumulativeRows,
  deriveCumulativeSeries,
  extractBlocksByDate,
} from "../helpers";
import { Granularity } from "../types";
import { CUMULATIVE_ID } from "../components/ContractsSidebar";

const fmt = (n: number) =>
  Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "—";

const SOURCE_COLORS = [
  "#F1935C",
  "#0EA5A4",
  "#6366F1",
  "#2E90FA",
  "#B47817",
  "#E2682B",
  "#10B981",
  "#8B5CF6",
];

function MetricCard({
  label,
  value,
  unit,
  hint,
  hintTone,
  dotColor,
}: {
  label: string;
  value: string;
  unit?: string;
  hint?: string;
  hintTone?: "neg" | "muted";
  dotColor: string;
}) {
  return (
    <div className="bg-white border border-[#E8EAEF] rounded-[10px] px-3.5 py-3 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-wider text-[#828A99] font-semibold">
          {label}
        </span>
        <span
          className="w-2 h-2 rounded-full inline-block"
          style={{ background: dotColor }}
        />
      </div>
      <div className="text-[24px] font-bold text-[#525062] leading-tight">
        {value}
        {unit && (
          <span className="text-[12px] font-medium text-[#828A99] ml-1">
            {unit}
          </span>
        )}
      </div>
      {hint && (
        <div
          className={`text-[11px] ${
            hintTone === "neg"
              ? "text-[#E0463C] font-medium"
              : "text-[#828A99]"
          }`}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function ContributionCard({
  title,
  subtitle,
  items,
  scheduledLabel = "SCHEDULED SUPPLY",
  actualLabel = "ACTUAL SUPPLY",
  reverseDirection = false,
}: {
  title: string;
  subtitle: string;
  items: BankingSupplySource[];
  scheduledLabel?: string;
  actualLabel?: string;
  reverseDirection?: boolean;
}) {
  const maxShare = Math.max(...items.map((s) => s.share_pct), 1);
  return (
    <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-4 py-3.5 mb-3.5">
      <div className="mb-3">
        <div className="text-[13px] font-semibold text-[#525062]">{title}</div>
        <div className="text-[11px] text-[#828A99] mt-0.5">{subtitle}</div>
      </div>
      <div className="flex flex-col gap-4">
        {items.map((src, idx) => {
          const color = SOURCE_COLORS[idx % SOURCE_COLORS.length];
          const barWidth = maxShare > 0 ? (src.share_pct / maxShare) * 100 : 0;
          const from = reverseDirection ? "CSPDCL" : src.counterparty;
          const to = reverseDirection ? src.counterparty : "CSPDCL";
          return (
            <div key={src.counterparty}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: color }}
                  />
                  <span className="text-[13px] font-medium text-[#525062] truncate">
                    {from}
                  </span>
                  <ArrowRight size={13} className="text-[#E2682B] shrink-0" />
                  <span className="text-[13px] font-medium text-[#525062] truncate">
                    {to}
                  </span>
                </div>
                <div className="flex items-start gap-6 shrink-0 ml-4">
                  <div className="text-right">
                    <div className="text-[10px] font-semibold tracking-wider text-[#9B96A8] uppercase">
                      {scheduledLabel}
                    </div>
                    <div className="text-[13px] font-semibold text-[#525062] tabular-nums">
                      {fmt(src.scheduled_mwh)}{" "}
                      <span className="text-[11px] font-normal text-[#828A99]">
                        MWh
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold tracking-wider text-[#9B96A8] uppercase">
                      {actualLabel}
                    </div>
                    <div className="text-[13px] font-semibold text-[#525062] tabular-nums">
                      {fmt(src.actual_mwh)}{" "}
                      <span className="text-[11px] font-normal text-[#828A99]">
                        MWh
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold tracking-wider text-[#9B96A8] uppercase">
                      SHARE
                    </div>
                    <div className="text-[13px] font-semibold text-[#525062] tabular-nums">
                      {src.share_pct.toFixed(1)}%
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold tracking-wider text-[#9B96A8] uppercase">
                      VARIANCE
                    </div>
                    <div
                      className={`text-[13px] font-semibold tabular-nums ${
                        src.variance_pct < 0
                          ? "text-[#E0463C]"
                          : "text-[#1F8A39]"
                      }`}
                    >
                      {src.variance_pct >= 0 ? "+" : ""}
                      {src.variance_pct.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-1.5 bg-[#F4F5F8] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${barWidth}%`, background: color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CumulativeView({
  supplyAnalytics,
  returnAnalytics,
  supplySources,
  supplyDestinations,
  granularity,
  setGranularity,
}: {
  supplyAnalytics: BankingCumulativeSupplyAnalytics;
  returnAnalytics: BankingCumulativeReturnAnalytics | null;
  supplySources: BankingCumulativeSupplySources | null;
  supplyDestinations: BankingCumulativeSupplyDestinations | null;
  granularity: Granularity;
  setGranularity: (g: Granularity) => void;
}) {
  const { metrics } = supplyAnalytics;

  const showApprovalNos = useMemo(
    () =>
      [...supplyAnalytics.daily, ...(returnAnalytics?.daily ?? [])].some(
        (d) => (d.approval_nos ?? []).length > 0,
      ),
    [supplyAnalytics.daily, returnAnalytics?.daily],
  );
  const supplySeries = useMemo(
    () => deriveCumulativeSeries(supplyAnalytics.daily, granularity),
    [supplyAnalytics.daily, granularity],
  );

  const returnSeries = useMemo(
    () =>
      returnAnalytics
        ? deriveCumulativeSeries(returnAnalytics.daily, granularity)
        : [],
    [returnAnalytics, granularity],
  );

  const supplyRows = useMemo(
    () => deriveCumulativeRows(supplyAnalytics.daily, granularity),
    [supplyAnalytics.daily, granularity],
  );

  const returnRows = useMemo(
    () =>
      returnAnalytics
        ? deriveCumulativeRows(returnAnalytics.daily, granularity)
        : [],
    [returnAnalytics, granularity],
  );

  const supplyBlocks = useMemo(
    () => extractBlocksByDate(supplyAnalytics.daily),
    [supplyAnalytics.daily],
  );

  const returnBlocks = useMemo(
    () => (returnAnalytics ? extractBlocksByDate(returnAnalytics.daily) : {}),
    [returnAnalytics],
  );

  const isDrilldown = granularity === "Day" || granularity === "Time Block";

  const varPct =
    metrics.total_scheduled_mwh > 0
      ? ((metrics.total_actual_mwh - metrics.total_scheduled_mwh) /
          metrics.total_scheduled_mwh) *
        100
      : 0;

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-3 gap-3 mb-3.5 mt-3.5">
        <MetricCard
          label="TOTAL SCHEDULED"
          value={fmt(metrics.total_scheduled_mwh)}
          unit="MWh"
          dotColor="#2E90FA"
        />
        <MetricCard
          label="TOTAL ACTUAL"
          value={fmt(metrics.total_actual_mwh)}
          unit="MWh"
          hint={`${varPct >= 0 ? "+" : ""}${varPct.toFixed(2)}% vs scheduled`}
          hintTone={varPct < 0 ? "neg" : "muted"}
          dotColor="#F1935C"
        />
        <MetricCard
          label="RETURN REQUIRED"
          value={fmt(metrics.total_return_required_mwh)}
          unit="MWh"
          dotColor="#6366F1"
        />
      </div>

      <SupplyChart
        series={supplySeries}
        granularity={granularity}
        setGranularity={setGranularity}
        title="Cumulative supply: actual vs scheduled"
      />

      {supplySources && supplySources.sources.length > 0 && (
        <ContributionCard
          title="Contribution by contract"
          subtitle="How each contract weighs into the aggregate"
          items={supplySources.sources}
          scheduledLabel="SCHEDULED SUPPLY"
          actualLabel="ACTUAL SUPPLY"
        />
      )}

      <PeriodBreakdownTable
        rows={supplyRows}
        contractId={CUMULATIVE_ID}
        enableExpand={isDrilldown}
        preloadedBlocks={granularity === "Time Block" ? supplyBlocks : undefined}
        title="Supply breakdown"
        granularity={granularity}
        showApprovalNos={showApprovalNos}
      />

      {returnAnalytics && (
        <>
          <div className="mt-2 mb-2 flex items-center gap-2">
            <span className="h-px flex-1 bg-[#E8EAEF]" />
            <span className="text-[12px] font-semibold tracking-wider text-[#828A99] uppercase">
              Return
            </span>
            <span className="h-px flex-1 bg-[#E8EAEF]" />
          </div>

          <div className="grid grid-cols-4 gap-3 mb-3.5 mt-3.5">
            <MetricCard
              label="RETURN REQUIRED"
              value={fmt(returnAnalytics.metrics.total_return_required_mwh)}
              unit="MWh"
              dotColor="#6366F1"
            />
            <MetricCard
              label="RETURN GIVEN"
              value={fmt(returnAnalytics.metrics.total_return_given_actual_mwh)}
              unit="MWh"
              hint={`Scheduled ${fmt(returnAnalytics.metrics.total_return_given_scheduled_mwh)} MWh`}
              dotColor="#0EA5A4"
            />
            <MetricCard
              label="RETURN LEFT"
              value={fmt(returnAnalytics.metrics.total_return_left_mwh)}
              unit="MWh"
              hintTone={
                returnAnalytics.metrics.total_return_left_mwh > 0
                  ? "neg"
                  : "muted"
              }
              dotColor="#E2682B"
            />
            <MetricCard
              label="COVERAGE"
              value={
                returnAnalytics.metrics.total_return_required_mwh > 0
                  ? `${(
                      (returnAnalytics.metrics.total_return_given_actual_mwh /
                        returnAnalytics.metrics.total_return_required_mwh) *
                      100
                    ).toFixed(1)}`
                  : "—"
              }
              unit="%"
              dotColor="#B47817"
            />
          </div>

          <SupplyChart
            series={returnSeries}
            granularity={granularity}
            setGranularity={setGranularity}
            title="Cumulative return: actual vs scheduled"
          />

          {supplyDestinations && supplyDestinations.destinations.length > 0 && (
            <ContributionCard
              title="Return by contract"
              subtitle="How each contract contributes to the return"
              items={supplyDestinations.destinations}
              scheduledLabel="SCHEDULED RETURN"
              actualLabel="ACTUAL RETURN"
              reverseDirection={true}
            />
          )}

          <PeriodBreakdownTable
            rows={returnRows}
            contractId={CUMULATIVE_ID}
            enableExpand={isDrilldown}
            preloadedBlocks={granularity === "Time Block" ? returnBlocks : undefined}
            mode="return"
            title="Return breakdown"
            granularity={granularity}
            showApprovalNos={showApprovalNos}
              />
        </>
      )}
    </div>
  );
}

export default CumulativeView;
